import { fabric } from 'fabric';
import { missingFieldsFor } from './placeholder-key-registry';
import { fitTextToBox } from './text-fit';
import type {
  PlaceholderData,
  PlaceholderGeometry,
  PlaceholderImageData,
  PlaceholderTextData,
} from './types';

const OUTLINE_COLOR = 'rgba(99, 102, 241, 0.9)';
const WARNING_OUTLINE_COLOR = 'rgba(220, 38, 38, 0.9)';
const WARNING_FILL_COLOR = 'rgba(220, 38, 38, 0.08)';
const FILL_COLOR = 'rgba(99, 102, 241, 0.06)';
const BADGE_BG = '#6366f1';
const BADGE_FG = '#ffffff';
const LABEL_COLOR = '#4338ca';
const DASH_ARRAY = [8, 4];
const STROKE_WIDTH = 2;
const BADGE_HEIGHT = 20;
const BADGE_PADDING_X = 6;
const LABEL_FONT_SIZE = 12;
const HINT_COLOR = 'rgba(67, 56, 202, 0.55)';

const COMMON_GROUP_PROPS: Partial<fabric.IGroupOptions> = {
  originX: 'left',
  originY: 'top',
  hasRotatingPoint: true,
  objectCaching: false,
  subTargetCheck: false,
  lockUniScaling: false,
};

export function createPlaceholder(data: PlaceholderData, geometry: PlaceholderGeometry): fabric.Group {
  if (data.kind === 'placeholder-image') {
    return createImagePlaceholder(data, geometry);
  }
  return createTextPlaceholder(data, geometry);
}

export function createImagePlaceholder(data: PlaceholderImageData, geometry: PlaceholderGeometry): fabric.Group {
  const width = Math.max(20, geometry.width);
  const height = Math.max(20, geometry.height);
  if (data.previewMode) {
    return createImagePreviewLoadingGroup(data, geometry, width, height);
  }
  const isMissing = missingFieldsFor(data).length > 0;
  const children: fabric.Object[] = [
    backgroundRect(width, height, isMissing),
    ...badge('IMG', width),
    labelText(data, width),
    centerHint('IMAGE', width, height),
  ];
  return assembleGroup(children, data, geometry, width, height);
}

export function createTextPlaceholder(data: PlaceholderTextData, geometry: PlaceholderGeometry): fabric.Group {
  const width = Math.max(20, geometry.width);
  const height = Math.max(20, geometry.height);
  if (data.resolvedText !== undefined || data.previewMode) {
    return createTextPreviewGroup(data, geometry, width, height);
  }
  const innerWidth = Math.max(8, width - 12);
  const innerHeight = Math.max(8, height - BADGE_HEIGHT - LABEL_FONT_SIZE - 16);

  const fontSize = fitTextToBox({
    text: data.sampleText || 'Sample',
    width: innerWidth,
    height: innerHeight,
    fontFamily: data.fontFamily,
    fontWeight: data.fontWeight,
    fontStyle: data.fontStyle,
    maxFontSize: data.maxFontSize,
    minFontSize: data.minFontSize,
  });

  const sample = new fabric.Text(data.sampleText || 'Sample', {
    fontSize,
    fontFamily: data.fontFamily,
    fontWeight: data.fontWeight,
    fontStyle: normalizeFontStyle(data.fontStyle),
    fill: data.fill,
    originX: 'center',
    originY: 'center',
    left: width / 2,
    top: (height + BADGE_HEIGHT + LABEL_FONT_SIZE) / 2 + 2,
    textAlign: data.textAlign,
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  const isMissing = missingFieldsFor(data).length > 0;
  const children: fabric.Object[] = [
    backgroundRect(width, height, isMissing),
    ...badge('T', width),
    labelText(data, width),
    sample,
  ];
  return assembleGroup(children, data, geometry, width, height);
}

function createTextPreviewGroup(
  data: PlaceholderTextData,
  geometry: PlaceholderGeometry,
  width: number,
  height: number,
): fabric.Group {
  const display = data.resolvedText ?? data.sampleText ?? 'Sample';
  const fontSize = fitTextToBox({
    text: display || 'Sample',
    width,
    height,
    fontFamily: data.fontFamily,
    fontWeight: data.fontWeight,
    fontStyle: data.fontStyle,
    maxFontSize: data.maxFontSize,
    minFontSize: data.minFontSize,
  });

  const sample = new fabric.Text(display || 'Sample', {
    fontSize,
    fontFamily: data.fontFamily,
    fontWeight: data.fontWeight,
    fontStyle: normalizeFontStyle(data.fontStyle),
    fill: data.fill,
    originX: 'center',
    originY: 'center',
    left: width / 2,
    top: height / 2,
    textAlign: data.textAlign,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  return assembleGroup([sample], data, geometry, width, height);
}

function createImagePreviewLoadingGroup(
  data: PlaceholderImageData,
  geometry: PlaceholderGeometry,
  width: number,
  height: number,
): fabric.Group {
  const bg = new fabric.Rect({
    left: 0,
    top: 0,
    width,
    height,
    fill: 'rgba(99, 102, 241, 0.04)',
    stroke: OUTLINE_COLOR,
    strokeWidth: 1,
    strokeDashArray: [4, 4],
    strokeUniform: true,
    rx: 4,
    ry: 4,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  const hint = new fabric.Text('loading…', {
    left: width / 2,
    top: height / 2,
    originX: 'center',
    originY: 'center',
    fontSize: Math.max(11, Math.min(width, height) * 0.08),
    fontFamily: 'Arial',
    fill: HINT_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  return assembleGroup([bg, hint], data, geometry, width, height);
}

export function createImagePreviewGroup(
  data: PlaceholderImageData,
  geometry: PlaceholderGeometry,
  image: fabric.Image,
): fabric.Group {
  const width = Math.max(20, geometry.width);
  const height = Math.max(20, geometry.height);
  const naturalW = (image.getElement() as HTMLImageElement | null)?.naturalWidth || image.width || 1;
  const naturalH = (image.getElement() as HTMLImageElement | null)?.naturalHeight || image.height || 1;

  // Reset fabric.Image to natural dims (clears any prior crop / scale from cache reuse).
  image.set({
    width: naturalW,
    height: naturalH,
    cropX: 0,
    cropY: 0,
  });

  if (data.fitMode === 'cover') {
    // Why: 'cover' must clip to placeholder bounds. Use fabric.Image's built-in
    // cropX/cropY/width/height (in source-pixel space) instead of clipPath —
    // clipPath inside a Group has fragile coordinate semantics.
    const scale = Math.max(width / naturalW, height / naturalH);
    const cropW = width / scale;
    const cropH = height / scale;
    image.set({
      cropX: Math.max(0, (naturalW - cropW) / 2),
      cropY: Math.max(0, (naturalH - cropH) / 2),
      width: cropW,
      height: cropH,
      scaleX: scale,
      scaleY: scale,
    });
  } else {
    const scale = Math.min(width / naturalW, height / naturalH);
    image.set({ scaleX: scale, scaleY: scale });
  }

  image.set({
    left: width / 2,
    top: height / 2,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  return assembleGroup([image], data, geometry, width, height);
}

export function buildPreviewImageUrl(data: PlaceholderImageData, width: number, height: number): string {
  const seed = previewSeedFor(data);
  const w = Math.max(2, Math.round(width));
  const h = Math.max(2, Math.round(height));
  // Why: synchronous, network-free preview. Picsum (or any external host)
  // can stall or be blocked by CSP/firewalls, leaving the placeholder stuck
  // on "loading…". A data URL always resolves and renders the same image for
  // the same seed/size.
  return buildPreviewDataUrl(seed, w, h);
}

export function previewSeedFor(data: PlaceholderImageData): string {
  const parts: string[] = [];
  if (data.placeholderKey) parts.push(data.placeholderKey);
  if (typeof data.teamNumber === 'number') parts.push(`t${data.teamNumber}`);
  if (typeof data.playerNumber === 'number') parts.push(`p${data.playerNumber}`);
  if (parts.length === 0 && data.previewSeed) parts.push(data.previewSeed);
  if (parts.length === 0) parts.push('placeholder');
  return parts.join('_');
}

function buildPreviewDataUrl(seed: string, width: number, height: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const hash = hashString(seed);
  const hueA = hash % 360;
  const hueB = (hueA + 40 + (hash % 80)) % 360;
  const grad = ctx.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, `hsl(${hueA}, 65%, 62%)`);
  grad.addColorStop(1, `hsl(${hueB}, 55%, 38%)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  ctx.save();
  ctx.globalAlpha = 0.08;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(0, height * 0.65);
  ctx.lineTo(width, height * 0.25);
  ctx.lineTo(width, height * 0.45);
  ctx.lineTo(0, height * 0.85);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  const fontSize = Math.max(12, Math.min(width, height) * 0.16);
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
  ctx.fillText(seed, width / 2, height / 2);

  return canvas.toDataURL('image/png');
}

function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

function backgroundRect(width: number, height: number, isMissingData = false): fabric.Rect {
  return new fabric.Rect({
    left: 0,
    top: 0,
    width,
    height,
    fill: isMissingData ? WARNING_FILL_COLOR : FILL_COLOR,
    stroke: isMissingData ? WARNING_OUTLINE_COLOR : OUTLINE_COLOR,
    strokeWidth: STROKE_WIDTH,
    strokeDashArray: DASH_ARRAY,
    strokeUniform: true,
    rx: 4,
    ry: 4,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

function badge(text: string, width: number): fabric.Object[] {
  const natural = text.length * 7 + BADGE_PADDING_X * 2;
  const labelWidth = Math.max(0, Math.min(natural, width - 4));
  const bg = new fabric.Rect({
    left: 0,
    top: 0,
    width: labelWidth,
    height: BADGE_HEIGHT,
    fill: BADGE_BG,
    selectable: false,
    evented: false,
    objectCaching: false,
    rx: 2,
    ry: 2,
  });
  const label = new fabric.Text(text, {
    left: labelWidth / 2,
    top: BADGE_HEIGHT / 2,
    originX: 'center',
    originY: 'center',
    fontSize: 11,
    fontFamily: 'Arial',
    fontWeight: 'bold',
    fill: BADGE_FG,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  return [bg, label];
}

function labelText(data: PlaceholderData, width: number): fabric.Text {
  const measure = makeLabelMeasure();
  const display = truncateTextToWidth(composeIdentityLabel(data), Math.max(0, width - 12), measure);
  return new fabric.Text(display, {
    left: 6,
    top: BADGE_HEIGHT + 2,
    fontSize: LABEL_FONT_SIZE,
    fontFamily: 'Arial',
    fontWeight: 'normal',
    fill: LABEL_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

let labelMeasureCtx: CanvasRenderingContext2D | null = null;

function makeLabelMeasure(): (s: string) => number {
  if (!labelMeasureCtx) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = `normal ${LABEL_FONT_SIZE}px Arial`;
      labelMeasureCtx = ctx;
    }
  }
  const ctx = labelMeasureCtx;
  if (ctx) {
    return (s) => ctx.measureText(s).width;
  }
  // Fallback when no 2D context is available (e.g. unusual headless env).
  const charWidth = LABEL_FONT_SIZE * 0.7;
  return (s) => s.length * charWidth;
}

export function truncateTextToWidth(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
): string {
  if (maxWidth <= 0) return '';
  if (measure(text) <= maxWidth) return text;
  const ellipsis = '…';
  if (measure(ellipsis) > maxWidth) return ellipsis;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (measure(text.slice(0, mid) + ellipsis) <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo === 0 ? ellipsis : text.slice(0, lo) + ellipsis;
}

function composeIdentityLabel(data: PlaceholderData): string {
  const parts: string[] = [];
  const baseKey = data.placeholderKey ? data.placeholderKey : '(unset key)';
  parts.push(baseKey);
  if (typeof data.teamNumber === 'number') parts.push(`team #${data.teamNumber}`);
  if (typeof data.playerNumber === 'number') parts.push(`player #${data.playerNumber}`);
  const right = data.placeholderLabel ? ` — ${data.placeholderLabel}` : '';
  return parts.join(' · ') + right;
}

function centerHint(text: string, width: number, height: number): fabric.Text {
  return new fabric.Text(text, {
    left: width / 2,
    top: (height + BADGE_HEIGHT + LABEL_FONT_SIZE) / 2 + 2,
    originX: 'center',
    originY: 'center',
    fontSize: Math.max(12, Math.min(width, height) * 0.18),
    fontFamily: 'Arial',
    fontWeight: 'bold',
    fill: HINT_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

function assembleGroup(
  children: fabric.Object[],
  data: PlaceholderData,
  geometry: PlaceholderGeometry,
  width: number,
  height: number,
): fabric.Group {
  const group = new fabric.Group(children, {
    ...COMMON_GROUP_PROPS,
    left: geometry.left ?? 100,
    top: geometry.top ?? 100,
    angle: geometry.angle ?? 0,
    width,
    height,
  });
  // Why: fabric.Group computes its own width/height from children bbox during
  // construction; force the intended size so subsequent toJSON / refit math is
  // anchored on the visual rect, not the bbox of the children.
  group.set({ width, height });
  // Why: text-width measurement can still under-truncate in real font stacks
  // (substituted fonts, kerning) for very narrow placeholders, leaving the
  // label visually escaping the rectangle. A clipPath sized to the group
  // guarantees no child renders past the placeholder bounds.
  group.clipPath = new fabric.Rect({
    left: 0,
    top: 0,
    width,
    height,
    originX: 'center',
    originY: 'center',
    absolutePositioned: false,
  });
  group.data = { ...data };
  group.setCoords();
  return group;
}

export function getPlaceholderData(group: fabric.Object | undefined | null): PlaceholderData | undefined {
  const data = (group as fabric.Object | undefined)?.data as PlaceholderData | undefined;
  if (!data) return undefined;
  if (data.kind !== 'placeholder-image' && data.kind !== 'placeholder-text') return undefined;
  return data;
}

export function getScaledSize(group: fabric.Group): { width: number; height: number } {
  const w = (group.width ?? 0) * (group.scaleX ?? 1);
  const h = (group.height ?? 0) * (group.scaleY ?? 1);
  return { width: w, height: h };
}

function normalizeFontStyle(value: string | undefined): '' | 'normal' | 'italic' | 'oblique' {
  if (value === 'italic' || value === 'oblique' || value === 'normal' || value === '') return value;
  return 'normal';
}
